import React, { useEffect, useState, useCallback } from "react";
import * as turf from "@turf/turf";
import {
    VisibleFeature,
    IVisibleFeatureType,
    VisibleFeatureType,
    FeatureGeometry,
} from "../../../../types/types";

type SelectionTool = "Rectangle" | "Circle" | "Lasso" | null;
type SelectionMode = "within" | "contained";
type BoundsArray = [[number, number], [number, number]];

interface MapSelectionToolsProps {
    map: mapboxgl.Map;
    activeTool: SelectionTool;
    selectionMode: SelectionMode;
    onSelectionComplete: (params: {
        bounds: BoundsArray;
        features: VisibleFeature[];
        geometry: GeoJSON.Feature;
    }) => void;
    onRequestToolReset?: () => void;
    mapLayerIds: Map<string, IVisibleFeatureType>;
}

const SELECTION_LAYER_ID = "selection-layer";
const SELECTION_SOURCE_ID = "selection-source";

const constrainCircleToMapBounds = (
    center: [number, number],
    radius: number,
    map: mapboxgl.Map
): number => {
    const bounds = map.getBounds();
    if (!bounds) return radius;

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    const distToWest = turf.distance(
        turf.point(center),
        turf.point([sw.lng, center[1]]),
        { units: "kilometers" }
    );
    const distToEast = turf.distance(
        turf.point(center),
        turf.point([ne.lng, center[1]]),
        { units: "kilometers" }
    );
    const distToSouth = turf.distance(
        turf.point(center),
        turf.point([center[0], sw.lat]),
        { units: "kilometers" }
    );
    const distToNorth = turf.distance(
        turf.point(center),
        turf.point([center[0], ne.lat]),
        { units: "kilometers" }
    );

    const minDistToEdge = Math.min(
        distToWest,
        distToEast,
        distToSouth,
        distToNorth
    );

    return Math.min(radius, minDistToEdge);
};

const initializeSelectionLayer = (map: mapboxgl.Map) => {
    if (!map.getSource(SELECTION_SOURCE_ID)) {
        map.addSource(SELECTION_SOURCE_ID, {
            type: "geojson",
            data: {
                type: "Feature",
                geometry: { type: "Polygon", coordinates: [[]] },
                properties: {},
            },
        });
    }

    if (!map.getLayer(SELECTION_LAYER_ID)) {
        map.addLayer({
            id: SELECTION_LAYER_ID,
            type: "fill",
            source: SELECTION_SOURCE_ID,
            paint: {
                "fill-color": "#3B82F6",
                "fill-opacity": 0.2,
            },
        });

        map.addLayer({
            id: `${SELECTION_LAYER_ID}-outline`,
            type: "line",
            source: SELECTION_SOURCE_ID,
            paint: {
                "line-color": "#3B82F6",
                "line-width": 2,
            },
        });
    }
};

const updateSelectionGeometry = (
    map: mapboxgl.Map,
    geometry: GeoJSON.Feature
) => {
    const source = map.getSource(SELECTION_SOURCE_ID) as mapboxgl.GeoJSONSource;
    if (source) {
        source.setData(geometry);
    }
};

const enableMapInteractions = (map: mapboxgl.Map) => {
    map.dragPan.enable();
    map.doubleClickZoom.enable();
    map.scrollZoom.enable();
    map.dragRotate.enable();
    map.keyboard.enable();
};

const disableMapInteractions = (map: mapboxgl.Map) => {
    map.dragPan.disable();
    map.doubleClickZoom.disable();
    map.scrollZoom.disable();
    map.dragRotate.disable();
    map.keyboard.disable();
};

const MapSelectionTools: React.FC<MapSelectionToolsProps> = ({
    map,
    activeTool,
    selectionMode,
    onSelectionComplete,
    onRequestToolReset,
    mapLayerIds,
}) => {
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPoint, setStartPoint] = useState<mapboxgl.Point | null>(null);
    const [points, setPoints] = useState<mapboxgl.Point[]>([]);
    const [lastGeometry, setLastGeometry] = useState<GeoJSON.Feature | null>(
        null
    );
    const [selectionComplete, setSelectionComplete] = useState(false);

    // Store mutable references
    const stateRef = React.useRef({
        isDrawing,
        startPoint,
        points,
        activeTool,
        map,
        selectionComplete,
    });

    // Store onSelectionComplete in a ref to prevent unnecessary re-renders
    const onSelectionCompleteRef = React.useRef(onSelectionComplete);
    React.useEffect(() => {
        onSelectionCompleteRef.current = onSelectionComplete;
    }, [onSelectionComplete]);

    // Store onRequestToolReset in a ref to prevent unnecessary re-renders
    const onRequestToolResetRef = React.useRef(onRequestToolReset);
    React.useEffect(() => {
        onRequestToolResetRef.current = onRequestToolReset;
    }, [onRequestToolReset]);

    // Calculates all features within the selection geometry
    const calculateSelectedFeatures = useCallback(
        (geometry: GeoJSON.Feature) => {
            const bbox = turf.bbox(geometry);
            const bounds: BoundsArray = [
                [bbox[0], bbox[1]],
                [bbox[2], bbox[3]],
            ];

            const sw = map.project({
                lng: bounds[0][0],
                lat: bounds[0][1],
            });
            const ne = map.project({
                lng: bounds[1][0],
                lat: bounds[1][1],
            });
            const features = map.queryRenderedFeatures([sw, ne]);

            const selectedFeatures = features
                .filter((feature) => {
                    if (!feature.geometry) return false;

                    // Handle MultiLineString geometries by converting to LineString features
                    if (feature.geometry.type === "MultiLineString") {
                        const lineStrings = feature.geometry.coordinates.map(
                            (coords) => ({
                                type: "Feature",
                                geometry: {
                                    type: "LineString" as const,
                                    coordinates: coords,
                                },
                                properties: feature.properties,
                            })
                        );

                        if (selectionMode === "contained") {
                            return lineStrings.every((lineString) =>
                                turf.booleanContains(
                                    turf.feature(geometry.geometry),
                                    turf.feature(lineString.geometry)
                                )
                            );
                        } else {
                            return lineStrings.some((lineString) =>
                                turf.booleanIntersects(
                                    turf.feature(geometry.geometry),
                                    turf.feature(lineString.geometry)
                                )
                            );
                        }
                    }

                    if (feature.geometry.type === "MultiPolygon") {
                        const polygons = feature.geometry.coordinates.map(
                            (coords) => ({
                                type: "Feature",
                                geometry: {
                                    type: "Polygon" as const,
                                    coordinates: coords,
                                },
                                properties: feature.properties,
                            })
                        );

                        if (selectionMode === "contained") {
                            return polygons.every((polygon) =>
                                turf.booleanContains(
                                    turf.feature(geometry.geometry),
                                    turf.feature(polygon.geometry)
                                )
                            );
                        } else {
                            return polygons.some((polygon) =>
                                turf.booleanIntersects(
                                    turf.feature(geometry.geometry),
                                    turf.feature(polygon.geometry)
                                )
                            );
                        }
                    }

                    if (selectionMode === "contained") {
                        return turf.booleanContains(
                            turf.feature(geometry.geometry),
                            turf.feature(feature.geometry)
                        );
                    }
                    return turf.booleanIntersects(
                        turf.feature(geometry.geometry),
                        turf.feature(feature.geometry)
                    );
                })
                .map((feature) => ({
                    id: feature.id?.toString() || `feature-${Math.random()}`,
                    type:
                        mapLayerIds.get(feature.layer?.id || "") ||
                        VisibleFeatureType.other,
                    properties: feature.properties || {},
                    geometry: feature.geometry as FeatureGeometry,
                }));

            return { bounds, features: selectedFeatures };
        },
        [map, selectionMode, mapLayerIds]
    );

    // Update refs when props/state change
    useEffect(() => {
        stateRef.current = {
            isDrawing,
            startPoint,
            points,
            activeTool,
            map,
            selectionComplete,
        };
    }, [isDrawing, startPoint, points, activeTool, map, selectionComplete]);

    const handleMouseDown = useCallback((e: mapboxgl.MapMouseEvent) => {
        const { activeTool, map } = stateRef.current;
        if (!activeTool) return;

        e.preventDefault();
        setIsDrawing(true);
        setStartPoint(e.point);

        if (activeTool === "Lasso") {
            setPoints([e.point]);
        } else {
            updateSelectionGeometry(
                map,
                turf.feature({
                    type: "Polygon",
                    coordinates: [[]],
                })
            );
        }
    }, []);

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
        const { isDrawing, startPoint, points, activeTool, map } =
            stateRef.current;

        if (!isDrawing || !startPoint || !activeTool) {
            return;
        }

        const currentPoint = e.point;
        let geometry;

        switch (activeTool) {
            case "Rectangle": {
                const minX = Math.min(startPoint.x, currentPoint.x);
                const maxX = Math.max(startPoint.x, currentPoint.x);
                const minY = Math.min(startPoint.y, currentPoint.y);
                const maxY = Math.max(startPoint.y, currentPoint.y);

                const sw = map.unproject([minX, maxY]);
                const ne = map.unproject([maxX, minY]);

                geometry = turf.bboxPolygon([sw.lng, sw.lat, ne.lng, ne.lat]);
                break;
            }
            case "Circle": {
                const centerLngLat = map.unproject(startPoint);
                const pointLngLat = map.unproject(currentPoint);

                const rawRadius = turf.distance(
                    turf.point([centerLngLat.lng, centerLngLat.lat]),
                    turf.point([pointLngLat.lng, pointLngLat.lat]),
                    { units: "kilometers" }
                );

                const radius = constrainCircleToMapBounds(
                    [centerLngLat.lng, centerLngLat.lat],
                    rawRadius,
                    map
                );

                geometry = turf.circle(
                    [centerLngLat.lng, centerLngLat.lat],
                    radius,
                    { steps: 64, units: "kilometers" }
                );
                break;
            }
            case "Lasso": {
                setPoints((prev) => [...prev, currentPoint]);
                if (points.length >= 2) {
                    const coordinates = [...points, currentPoint].map(
                        (point) => {
                            const lngLat = map.unproject(point);
                            return [lngLat.lng, lngLat.lat];
                        }
                    );
                    geometry = turf.feature({
                        type: "Polygon",
                        coordinates: [coordinates],
                    });
                }
                break;
            }
        }

        if (geometry) {
            updateSelectionGeometry(map, geometry);
        }
    };

    const handleMouseUp = useCallback(
        (e: mapboxgl.MapMouseEvent) => {
            const { isDrawing, startPoint, points, activeTool, map } =
                stateRef.current;

            if (!isDrawing || !startPoint || !activeTool) {
                return;
            }

            let geometry: GeoJSON.Feature | null = null;

            switch (activeTool) {
                case "Rectangle": {
                    const currentPoint = e.point;
                    const minX = Math.min(startPoint.x, currentPoint.x);
                    const maxX = Math.max(startPoint.x, currentPoint.x);
                    const minY = Math.min(startPoint.y, currentPoint.y);
                    const maxY = Math.max(startPoint.y, currentPoint.y);

                    const sw = map.unproject([minX, maxY]);
                    const ne = map.unproject([maxX, minY]);

                    geometry = turf.bboxPolygon([
                        sw.lng,
                        sw.lat,
                        ne.lng,
                        ne.lat,
                    ]);
                    break;
                }
                case "Circle": {
                    const centerLngLat = map.unproject(startPoint);
                    const pointLngLat = map.unproject(e.point);

                    const rawRadius = turf.distance(
                        turf.point([centerLngLat.lng, centerLngLat.lat]),
                        turf.point([pointLngLat.lng, pointLngLat.lat]),
                        { units: "kilometers" }
                    );

                    const radius = constrainCircleToMapBounds(
                        [centerLngLat.lng, centerLngLat.lat],
                        rawRadius,
                        map
                    );

                    geometry = turf.circle(
                        [centerLngLat.lng, centerLngLat.lat],
                        radius,
                        { steps: 64, units: "kilometers" }
                    );
                    break;
                }
                case "Lasso": {
                    if (points.length >= 3) {
                        const coordinates = [...points, e.point, points[0]].map(
                            (point) => {
                                const lngLat = map.unproject(point);
                                return [lngLat.lng, lngLat.lat];
                            }
                        );

                        geometry = turf.feature({
                            type: "Polygon",
                            coordinates: [coordinates],
                        });
                    }
                    break;
                }
            }

            if (geometry) {
                const { bounds, features: selectedFeatures } =
                    calculateSelectedFeatures(geometry);
                onSelectionCompleteRef.current({
                    bounds,
                    features: selectedFeatures,
                    geometry,
                });
                updateSelectionGeometry(map, geometry);
                setLastGeometry(geometry);
            }

            setIsDrawing(false);
            setStartPoint(null);
            setPoints([]);
            setSelectionComplete(true);
            if (typeof onRequestToolResetRef.current === "function") {
                onRequestToolResetRef.current();
            }
        },
        [calculateSelectedFeatures]
    );

    useEffect(() => {
        if (!map || !activeTool) {
            // Re-enable map interactions when no tool is active
            if (map) {
                enableMapInteractions(map);
                if (map.getCanvas()) {
                    map.getCanvas().style.cursor = "grab";
                }
            }
            return;
        }

        if (!selectionComplete) {
            initializeSelectionLayer(map);
            map.getCanvas().style.cursor = "crosshair";

            disableMapInteractions(map);

            map.on("mousedown", handleMouseDown);
            map.on("mousemove", handleMouseMove);
            map.on("mouseup", handleMouseUp);
        } else {
            enableMapInteractions(map);
            map.getCanvas().style.cursor = "grab";
        }

        return () => {
            map.off("mousedown", handleMouseDown);
            map.off("mousemove", handleMouseMove);
            map.off("mouseup", handleMouseUp);

            if (!activeTool) {
                if (map.getLayer(`${SELECTION_LAYER_ID}-outline`)) {
                    map.removeLayer(`${SELECTION_LAYER_ID}-outline`);
                }
                if (map.getLayer(SELECTION_LAYER_ID)) {
                    map.removeLayer(SELECTION_LAYER_ID);
                }
                if (map.getSource(SELECTION_SOURCE_ID)) {
                    map.removeSource(SELECTION_SOURCE_ID);
                }

                map.getCanvas().style.cursor = "";
                enableMapInteractions(map);
            }
        };
    }, [map, activeTool, handleMouseDown, handleMouseUp, selectionComplete]);

    // Add effect to recalculate selection when mode changes
    useEffect(() => {
        if (!map || !lastGeometry) return;
        const { bounds, features: selectedFeatures } =
            calculateSelectedFeatures(lastGeometry);
        onSelectionCompleteRef.current({ bounds, features: selectedFeatures, geometry: lastGeometry });
    }, [selectionMode, calculateSelectedFeatures, map, lastGeometry]);

    // Reset selectionComplete when a new tool is activated. Reset lastGeometry when no tool is active.
    useEffect(() => {
        if (activeTool) {
            setSelectionComplete(false);
        } else {
            setLastGeometry(null);
        }
    }, [activeTool]);

    return null;
};

export default MapSelectionTools;
